import { Injectable, inject, Injector } from '@angular/core';
import { AlertButton } from '@ionic/angular';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Firestore, collection, doc, query, where, orderBy } from 'firebase/firestore';
import { TranslateService } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';
import { Observable, BehaviorSubject, combineLatest, defer, firstValueFrom, map, of, switchMap, catchError } from 'rxjs';
import { SplashScreen } from '@capacitor/splash-screen';
import { environment } from 'src/environments/environment';
import { catchPermissionDenied } from '../utils/firestore-error.utils';
import { castObservable } from './firestore-typed-helpers';
import { collectionDataObservable, docDataObservable, docRefObservable } from './firestore-helpers';
import { FIREBASE_FIRESTORE } from '../firebase-config';

import { Version } from 'src/app/interfaces/version';
import { Terms } from 'src/app/interfaces/terms';
import { Privacy } from 'src/app/interfaces/privacy';
import { Provision } from 'src/app/interfaces/provision';
import { Faq } from 'src/app/interfaces/faq';
import { Region } from 'src/app/interfaces/region';

import { UserService } from './user.service';
import { AppUiService } from './app-ui.service';
import { AppStateService } from './app-state.service';
import { ContentDataService } from './content-data.service';
import { FormattingUtilsService } from './formatting-utils.service';
import { PendingNotificationRedirectService } from './pending-notification-redirect.service';

interface SettingDoc {
  id?: string;
  value?: unknown;
  [key: string]: unknown;
}

interface LocalizedAgreement {
  content_nl?: string;
  content_en?: string;
}

interface LocalizedFaq {
  question?: string;
  question_nl?: string;
  question_en?: string;
  answer?: string;
  answer_nl?: string;
  answer_en?: string;
}

interface PushNotificationLike {
  data?: Record<string, string | undefined>;
  title?: string;
  body?: string;
  type?: string;
}

interface ValueControl {
  value: string | number | null | undefined;
  setValue(value: unknown, options?: { emitEvent?: boolean }): void;
}

interface BoolSetting {
  value: boolean;
}

interface IndexedRecord {
  [key: string]: unknown;
}

function readBooleanField(source: unknown, field: string): boolean | undefined {
  if (typeof source !== 'object' || source === null) {
    return undefined;
  }

  const value = Reflect.get(source, field);
  return typeof value === 'boolean' ? value : undefined;
}

function readMaintenanceToggle(source: unknown): boolean {
  const fields = ['value', 'enabled', 'maintenance', 'maintenace'];

  for (const field of fields) {
    const value = readBooleanField(source, field);
    if (value === true) {
      return true;
    }
  }

  return false;
}

@Injectable({
  providedIn: 'root'
})
export class GlobalService {
  private firestore = inject(FIREBASE_FIRESTORE);
  private sanitizer = inject(DomSanitizer);
  private injector = inject(Injector);
  public UserService = inject(UserService);
  public translate = inject(TranslateService);
  private appUiService = inject(AppUiService);
  private appStateService = inject(AppStateService);
  private contentDataService = inject(ContentDataService);
  private formattingUtils = inject(FormattingUtilsService);
  private pendingNotificationRedirect = inject(PendingNotificationRedirectService);

  public get toastController() {
    return this.appUiService['toastController'];
  }

  public get alertController() {
    return this.appUiService['alertController'];
  }
  public datePipe = inject(DatePipe);

  public env = environment;
  public isLoading: boolean = false;
  private readonly submitLocks = new Set<string>();
  private readonly emptyRegions: Region[] = [];
  public offline: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private splashScreenHidden = false;
  private splashScreenHidePromise: Promise<void> | null = null;

  setOffline(offline: boolean) {
    this.offline.next(offline);
  }

  getOffline(): Observable<boolean> {
    return this.offline.asObservable();
  }

  createFirestoreKey() {
    const newDocRef = doc(collection(this.firestore, 'dummy-collection'));
    return newDocRef.id;
  }

  isSubmitLocked(key: string): boolean {
    return this.submitLocks.has(key);
  }

  async runWithSubmitLock<T>(
    key: string,
    action: () => Promise<T>
  ): Promise<boolean> {
    if (this.submitLocks.has(key)) {
      return false;
    }

    this.submitLocks.add(key);
    try {
      await action();
      return true;
    } finally {
      this.submitLocks.delete(key);
    }
  }

  /**
   * Format a date using the current user's language locale.
   * Falls back to 'nl' (Dutch) if no language is set.
   * 
   * @param value - The date value to format
   * @param format - Angular date format string (e.g., 'dd MMMM yyyy')
   * @param timezone - Optional timezone
   * @returns Formatted date string
   */
  formatDate(value: string | number | Date | null | undefined, format?: string, timezone?: string): string | null {
    return this.formattingUtils.formatDate(value, format, timezone, this.resolveCurrentLang());
  }

  /**
   * Sanitize HTML content for safe rendering in templates.
   * Use with [innerHTML] bindings through the safeHtml pipe.
   * 
   * @param html - The HTML string to sanitize
   * @returns SafeHtml object safe for rendering
   */
  sanitizeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  isCurrentUserAdmin() {
    return false;
  }

  currentAppVersion() {
    return this.env.version ?? '0.0.0';
  }

  getActiveRegions(): Observable<Region[]> {
    const permissionDeniedSentinel = { kind: 'permission-denied' } as const;

    return defer(() => this.fetchActiveRegions()).pipe(
      catchError((error) => {
        if (error?.code === 'permission-denied') {
          return of(permissionDeniedSentinel as unknown as Region[]);
        }

        return of([]);
      }),
      switchMap((regions) => {
        if ((regions as unknown as { kind?: string }).kind === 'permission-denied') {
          return of([]);
        }

        if (regions.length > 0) {
          return of(regions);
        }

        return this.fetchAllRegions().pipe(
          map((allRegions) => allRegions
            .map((region) => this.normalizeRegion(region))
            .filter((region) => this.isRegionActive(region)))
        );
      })
    );
  }

  async showLoader() {
    this.isLoading = true;
    await this.appUiService.showLoader();
  }

  async dismissLoader() {
    if (this.isLoading) {
      this.isLoading = false;
      await this.appUiService.dismissLoader();
    }
    return Promise.resolve();
  }

  async dismissToast() {
    await this.appUiService.dismissToast();
  }

  getRegion(regionId: string): Observable<Region | undefined> {
    return defer(() => this.fetchRegion(regionId)).pipe(
      catchError((error) => {
        if (error?.code === 'permission-denied' || error?.code === 'unauthenticated') {
          return of(undefined);
        }

        return of(undefined);
      })
    );
  }

  getFaqList(key: string): Observable<Faq[]> {
    return this.contentDataService.getFaqList(key);
  }

  getFaqDetails(faqId: string): Observable<Faq | undefined> {
    return this.contentDataService.getFaqDetails(faqId);
  }

  getSetting(key: string): Observable<SettingDoc | undefined> {
    return this.contentDataService.getSetting(key) as Observable<SettingDoc | undefined>;
  }

  getBooleanSetting(key: string, defaultValue: boolean = false): Observable<boolean> {
    return this.getSetting(key).pipe(
      map((setting) => {
        if (typeof setting !== 'object' || setting === null) {
          return defaultValue;
        }

        const value = Reflect.get(setting, 'value');
        return typeof value === 'boolean' ? value : defaultValue;
      })
    );
  }

  getMaintenanceMode(): Observable<boolean> {
    return this.getSetting('maintenance').pipe(
      switchMap((maintenanceSetting) =>
        this.getSetting('maintenace').pipe(
          switchMap((legacyMaintenanceSetting) =>
            this.getSetting('settings').pipe(
              map((groupedSettings) => [maintenanceSetting, legacyMaintenanceSetting, groupedSettings]
                .some((setting) => this.readMaintenanceToggle(setting)))
            )
          )
        )
      )
    );
  }

  translateCode(code: string) {
    return this.appUiService.translateCode(code);
  }

  async presentDefaultToast(messageCode: string, durationMs: number = 5000) {
    await this.appUiService.presentDefaultToast(messageCode, durationMs);
  }

  /**
   * Display an error message as a dismissable toast notification.
   * Accepts either an Error object or a translation key string.
   * 
   * @param error - Error object or translation key (string)
   * @example
   * try {
   *   await myAsyncOperation();
   * } catch (error) {
   *   this.globalService.presentErrorToast(error);
   * }
   * 
   * // Or with a translation key:
   * this.globalService.presentErrorToast('client.orders.error.failed_to_save');
   */
  async presentErrorToast(error: unknown, durationMs: number = 5000) {
    await this.appUiService.presentErrorToast(error, durationMs);
  }

  async presentDefaultAlert(headerCode: string, subHeaderCode: string, messageCode: string, buttonCode: string) {
    await this.appUiService.presentDefaultAlert(headerCode, subHeaderCode, messageCode, buttonCode);
  }

  getActiveVersion(): Observable<Version[]> {
    return this.contentDataService.getActiveVersion();
  }

  getActiveTerms(): Observable<Terms[]> {
    return this.contentDataService.getActiveTerms();
  }

  getActivePrivacy(): Observable<Privacy[]> {
    return this.contentDataService.getActivePrivacy();
  }

  getActiveProvisions(): Observable<Provision[]> {
    return this.contentDataService.getActiveProvisions();
  }

  translateStatus(status: string, type: string): string {
    const key = `shared.order_status.${type}.${status}`;
    const translated = this.translate.instant(key);
    
    // If translation key doesn't exist, return unknown
    if (translated === key) {
      return this.translate.instant('shared.order_status.unknown');
    }
    
    return translated;
  }

  translateYesNo(yesno: boolean): string {
    return yesno ? this.translate.instant('shared.yesno.yes') : this.translate.instant('shared.yesno.no');
  }

  /**
   * Get language-appropriate content from an agreement object.
   * Returns content_nl for Dutch or content_en for English based on current language.
   * 
   * @param agreement - Agreement object with content_nl and content_en fields
   * @returns The appropriate content based on current language
   */
  getAgreementContent(agreement: LocalizedAgreement): string {
    const currentLang = this.resolveCurrentLang();
    return currentLang === 'nl' ? (agreement.content_nl || '') : (agreement.content_en || '');
  }

  /**
   * Get language-appropriate question from an FAQ object.
   * Returns question_nl for Dutch or question_en for English based on current language.
   * Falls back to question field if language-specific field is not available.
   * 
   * @param faq - FAQ object with question, question_nl and question_en fields
   * @returns The appropriate question based on current language
   */
  getFaqQuestion(faq: LocalizedFaq | null | undefined): string {
    if (!faq) return '';
    const currentLang = this.resolveCurrentLang();
    if (currentLang === 'en' && faq.question_en) {
      return faq.question_en;
    }
    return faq.question_nl || faq.question || '';
  }

  /**
   * Get language-appropriate answer from an FAQ object.
   * Returns answer_nl for Dutch or answer_en for English based on current language.
   * Falls back to answer field if language-specific field is not available.
   * 
   * @param faq - FAQ object with answer, answer_nl and answer_en fields
   * @returns The appropriate answer based on current language
   */
  getFaqAnswer(faq: LocalizedFaq | null | undefined): string {
    if (!faq) return '';
    const currentLang = this.resolveCurrentLang();
    if (currentLang === 'en' && faq.answer_en) {
      return faq.answer_en;
    }
    return faq.answer_nl || faq.answer || '';
  }

  private resolveCurrentLang(): string {
    return this.translate.getCurrentLang() || this.translate.getFallbackLang() || 'nl';
  }

  private readMaintenanceToggle(source: unknown): boolean {
    const fields = ['value', 'enabled', 'maintenance', 'maintenace'];

    for (const field of fields) {
      const value = this.readBooleanField(source, field);
      if (value === true) {
        return true;
      }
    }

    return false;
  }

  private readBooleanField(source: unknown, field: string): boolean | undefined {
    if (typeof source !== 'object' || source === null) {
      return undefined;
    }

    const value = Reflect.get(source, field);
    return typeof value === 'boolean' ? value : undefined;
  }

  private fetchActiveRegions(): Observable<Region[]> {
    return collectionDataObservable<Region>(this.firestore, (fs) => {
      const regionsCollection = collection(fs, 'regions');
      return query(regionsCollection, where('is_active', '==', true), orderBy('title'));
    }, { idField: 'id' }).pipe(
      map((regions) => regions.map((region) => this.normalizeRegion(region))),
      map((regions) => [...regions].sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''))),
      catchPermissionDenied(this.emptyRegions)
    );
  }

  private fetchAllRegions(): Observable<Region[]> {
    return collectionDataObservable<Region>(this.firestore, (fs) => {
      const regionsCollection = collection(fs, 'regions');
      return query(regionsCollection, orderBy('title'));
    }, { idField: 'id' }).pipe(
      map((regions) => regions.map((region) => this.normalizeRegion(region))),
      map((regions) => [...regions].sort((a, b) => (a.title ?? '').localeCompare(b.title ?? ''))),
      catchPermissionDenied(this.emptyRegions)
    );
  }

  private fetchRegion(regionId: string): Observable<Region | undefined> {
    return docDataObservable<Region>(this.firestore, `regions/${regionId}`, { idField: 'id' }).pipe(
      catchPermissionDenied<Region | undefined>(undefined)
    );
  }

  private normalizeRegion(region: Region): Region {
    const source = region as Region & {
      name?: unknown;
      active?: unknown;
      enabled?: unknown;
    };

    const title = typeof source.title === 'string' && source.title.trim() !== ''
      ? source.title
      : typeof source.name === 'string'
        ? source.name
        : '';

    const isActive = typeof source.is_active === 'boolean'
      ? source.is_active
      : typeof source.active === 'boolean'
        ? source.active
        : typeof source.enabled === 'boolean'
          ? source.enabled
          : true;

    return {
      ...region,
      title,
      is_active: isActive,
    };
  }

  private isRegionActive(region: Region): boolean {
    return region.is_active ?? true;
  }

  handlePushNotification(notification: PushNotificationLike, app_open: boolean) {
    console.log('Handling push notification, app_open:', app_open);
    
    // Detect notification structure - could be different on Android vs iOS
    let data: Record<string, string | undefined>;
    let type: string | undefined;
    
    if (notification.data) {
      // Structure with data field (expected structure)
      data = notification.data;
      type = data['type'];
      
      // Check for notification_foreground flags in data
      if (app_open && data['notification_foreground'] === "true") {
        // Check if the notification should be displayed in foreground
        if (data['notification_foreground_display'] !== "true") {
          return;
        }
      }
    } else {
      // Try to use root fields as fallback
      data = {
        title: notification.title,
        body: notification.body,
        type: notification.type || 'alert', // Default to alert if no type specified
      };
      type = data['type'];
    }

    if (type === 'redirect') {
      this.redirectOnPushNotification(notification, app_open);
    } else if (type === 'alert') {
      // Handle alert type notification
      this.alertOnPushNotification(notification);
    } else {
      // Default to alert for other types or missing type
      this.alertOnPushNotification(notification);
    }
  }

  async redirectOnPushNotification(notification: PushNotificationLike, app_open: boolean) {
    // Safely access properties with robust fallbacks
    const title = (notification.data?.['title'] ?? notification.title) || 'New Notification';
    const body = (notification.data?.['body'] ?? notification.body) || '';
    const page = notification.data?.['page'];

    // We need a page to navigate to
    if (!page) {
      return this.alertOnPushNotification(notification);
    }

    if (app_open) {
      const closeText = await firstValueFrom(
        this.translate.get('shared.buttons.close')
      );
      const buttons: (AlertButton)[] = [];

      // Only offer the "view" action when the redirect target differs from the current page
      if (window.location.pathname.normalize() !== page.normalize()) {
        const viewText = await firstValueFrom(
          this.translate.get('shared.buttons.view')
        );
        buttons.push({
          text: viewText,
          handler: async () => {
            await this.appStateService.navigateToNotificationPage(page, window.location.pathname);
          }
        });
      }

      buttons.push({
        text: closeText,
        role: 'cancel',
        cssClass: 'notification-close-button',
        handler: () => {
          console.log('Notification dismissed');
        }
      });

      const alert = await this.alertController.create({
        header: title,
        message: body,
        buttons
      });
      await alert.present();
    } else if (page) {
      // Let any pending default redirect (e.g. the public home page) defer to this target
      // in case it resolves before this navigation attempt completes.
      this.pendingNotificationRedirect.setPendingPage(page);
      await this.appStateService.navigateTo(page);
    }
  }

  async alertOnPushNotification(notification: PushNotificationLike) {
    // Safely access properties with robust fallbacks
    const title = (notification.data?.['title'] ?? notification.title) || 'New Notification';
    const body = (notification.data?.['body'] ?? notification.body) || '';
    const closeText = await firstValueFrom(
      this.translate.get('shared.buttons.close')
    );

    // Create alert options
    const alertOptions = {
      header: title,
      message: body,
      buttons: [
        {
          text: closeText,
          role: 'cancel',
          cssClass: 'notification-close-button',
          handler: () => {
            // console.log('Alert dismissed');
          }
        }
      ]
    };

    // Handle custom actions from notification data
    if (notification.data?.['click_action'] === 'FLUTTER_NOTIFICATION_CLICK') {
      // Custom handling for this action could go here
    }

    const alert = await this.alertController.create(alertOptions);
    await alert.present();
  }

  formatMoney(value: string | number) {
    return this.formattingUtils.formatMoney(value);
  }

  removeMoneyQuantity(target: ValueControl, value: number) {
    this.formattingUtils.removeMoneyQuantity(target, value);
  }

  addMoneyQuantity(target: ValueControl, value: number) {
    this.formattingUtils.addMoneyQuantity(target, value);
  }

  removeTimeQuantity(target: ValueControl, value: number) {
    this.formattingUtils.removeTimeQuantity(target, value);
  }

  addTimeQuantity(target: ValueControl, value: number) {
    this.formattingUtils.addTimeQuantity(target, value);
  }

  calculateDate(year: number) {
    return this.formattingUtils.calculateDate(year);
  }

  calculateMinuteDifferenceBetweenTimestampAndNow(timestamp: unknown) {
    return this.formattingUtils.calculateMinuteDifferenceBetweenTimestampAndNow(timestamp);
  }

  private async hideNativeSplashScreen() {
    await SplashScreen.hide();
  }

  async hideSplashScreen() {
    if (this.splashScreenHidden) {
      return;
    }

    if (this.splashScreenHidePromise) {
      await this.splashScreenHidePromise;
      return;
    }

    this.splashScreenHidden = true;
    this.splashScreenHidePromise = (async () => {
      try {
        await this.hideNativeSplashScreen();
      } catch (error) {
        console.error('Error hiding splash screen:', error);
      }
    })();

    try {
      await this.splashScreenHidePromise;
    } finally {
      this.splashScreenHidePromise = null;
    }
  }
}
